import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { isAuthenticatedAtom } from './store';
import { LoginPage } from './pages/LoginPage';
import { ChatPage } from './pages/ChatPage';
import { FamilyPage } from './pages/FamilyPage';
import { FamilyAdminPage } from './pages/FamilyAdminPage';
import { InvitesPage } from './pages/InvitesPage';
import { ProfilePage } from './pages/ProfilePage';
import { StarredPage } from './pages/StarredPage';
import { CallsPage } from './pages/CallsPage';
import { MentionsPage } from './pages/MentionsPage';
import { LinksPage } from './pages/LinksPage';
import { DocumentsPage } from './pages/DocumentsPage';
import { ListsPage } from './pages/ListsPage';
import { ListDetailPage } from './pages/ListDetailPage';
import { JoinFamilyPage } from './pages/JoinFamilyPage';
import { JoinLinkPage } from './pages/JoinLinkPage';
import { LinkSignInPage } from './pages/LinkSignInPage';
import { ReviewLinkPage } from './pages/ReviewLinkPage';
import { SettingsPage } from './pages/SettingsPage';
import { AdminLoginPage } from './pages/AdminLoginPage';
import { AdminPage } from './pages/AdminPage';
import { Layout } from './components/Layout';
import { LandingPage } from './pages/LandingPage';
import { PolicyPage } from './pages/PolicyPage';
import { PricingPage } from './pages/PricingPage';
import { RequestInvitePage } from './pages/RequestInvitePage';
import { AboutPage } from './pages/AboutPage';
import { InstallGuidePage } from './pages/InstallGuidePage';
import { HelpPage } from './pages/HelpPage';
import { SharePage } from './pages/SharePage';
import { IdeasPage } from './pages/IdeasPage';
import { pendingShare, rememberPendingShare } from './lib/shares';
import { useEffect } from 'react';
import { useLanguage } from './i18n';
import { useNotificationOpens } from './hooks/useNotificationOpens';
import { useAppModeReport } from './hooks/useAppModeReport';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAtomValue(isAuthenticatedAtom);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Share to Twinden (D178): signed out, the share waits on this device while
// you sign in, and every way of signing in lands on "/", which brings you back.
export function ShareRoute() {
  const isAuthenticated = useAtomValue(isAuthenticatedAtom);
  const { search } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (isAuthenticated) return;
    rememberPendingShare(search);
    navigate('/login', { replace: true });
  }, [isAuthenticated, search, navigate]);
  if (!isAuthenticated) return null;
  return (
    <Layout>
      <SharePage />
    </Layout>
  );
}

// "/" is the chats when signed in and the landing page otherwise (M42, D104).
export function HomeRoute() {
  const isAuthenticated = useAtomValue(isAuthenticatedAtom);
  if (!isAuthenticated) return <LandingPage />;
  const share = pendingShare();
  if (share !== null) return <Navigate to={`/share${share}`} replace />;
  return (
    <Layout>
      <ChatPage />
    </Layout>
  );
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAtomValue(isAuthenticatedAtom);

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  // The page's language follows the app's (D146), for screen readers and
  // the right Devanagari shaping.
  const language = useLanguage();
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);
  useNotificationOpens();
  useAppModeReport();

  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route path="/" element={<HomeRoute />} />
      {/* Public pages (M42): pricing and policies, signed in or not. */}
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/privacy" element={<PolicyPage slug="privacy" />} />
      <Route path="/terms" element={<PolicyPage slug="terms" />} />
      <Route path="/contact" element={<PolicyPage slug="contact" />} />
      <Route path="/request-invite" element={<RequestInvitePage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/install" element={<InstallGuidePage />} />
      {/* How-to guides and questions, public (#92, D290). */}
      <Route path="/help" element={<HelpPage />} />
      {/* Shape Twinden (M55, D198): read-only signed out, in the app signed in. */}
      <Route path="/ideas" element={<IdeasPage />} />
      <Route path="/share" element={<ShareRoute />} />
      <Route
        path="/family"
        element={
          <ProtectedRoute>
            <Layout>
              <FamilyPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/mentions"
        element={
          <ProtectedRoute>
            <Layout>
              <MentionsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/calls"
        element={
          <ProtectedRoute>
            <Layout>
              <CallsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/starred"
        element={
          <ProtectedRoute>
            <Layout>
              <StarredPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/links"
        element={
          <ProtectedRoute>
            <Layout>
              <LinksPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/documents"
        element={
          <ProtectedRoute>
            <Layout>
              <DocumentsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/lists"
        element={
          <ProtectedRoute>
            <Layout>
              <ListsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/lists/:listId"
        element={
          <ProtectedRoute>
            <Layout>
              <ListDetailPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/family/:familyId/admin"
        element={
          <ProtectedRoute>
            <Layout>
              <FamilyAdminPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/invites"
        element={
          <ProtectedRoute>
            <Layout>
              <InvitesPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Layout>
              <ProfilePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Layout>
              <SettingsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route path="/join/f/:secret" element={<JoinLinkPage />} />
      <Route path="/join/:token" element={<JoinFamilyPage />} />
      {/* Sign in by scanning, the phone's side (M49, D158) */}
      <Route path="/link/:code" element={<LinkSignInPage />} />
      {/* An app reviewer's review link (M56, D203) */}
      <Route path="/review/:token" element={<ReviewLinkPage />} />
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
